import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Car, ArrowLeft, Navigation2 } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoFooter } from "@/components/SeoFooter";
import { useSeo, ogImageUrl } from "@/lib/seo";
import { findArticle, ARTICLES, BLOG_PATH } from "@/lib/blog";
import { THEMATIC_PAGES } from "@/lib/thematic";

export default function BlogArticle() {
  const { slug } = useParams();
  const article = findArticle(slug);

  useSeo(
    article ? `${article.title} | vspo` : "Conseils VSP | vspo",
    article ? article.description : "",
    article ? `${BLOG_PATH}/${article.slug}` : undefined,
    article ? ogImageUrl({ title: article.title, subtitle: "Conseils VSP" }) : undefined
  );

  // Article structured data (JSON-LD) for rich results.
  useEffect(() => {
    if (!article) return undefined;
    const origin = window.location.origin;
    const data = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.description,
      datePublished: article.date,
      dateModified: article.date,
      inLanguage: "fr-FR",
      author: { "@type": "Organization", name: "vspo" },
      publisher: {
        "@type": "Organization",
        name: "vspo",
        logo: { "@type": "ImageObject", url: `${origin}/icon-512.png` },
      },
      mainEntityOfPage: `${origin}${BLOG_PATH}/${article.slug}`,
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "article-jsonld";
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [article]);

  if (!article) return <Navigate to={BLOG_PATH} replace />;

  const others = ARTICLES.filter((a) => a.slug !== article.slug);

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to={BLOG_PATH} className="flex items-center gap-2" data-testid="blog-back">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Car className="h-5 w-5" />
            </span>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-foreground">vspo</div>
              <div className="text-[11px] text-muted-foreground">Conseils VSP</div>
            </div>
          </Link>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Link to="/" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground" data-testid="article-open-app">
              <ArrowLeft className="h-4 w-4" /> Ouvrir le GPS
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-20 pt-6">
        <nav className="mb-3 text-xs text-muted-foreground" aria-label="Fil d'ariane">
          <Link to="/" className="hover:text-primary">Accueil</Link> ·{" "}
          <Link to={BLOG_PATH} className="hover:text-primary">Conseils</Link> ·{" "}
          <span className="text-foreground">{article.title}</span>
        </nav>

        <h1 className="font-display text-3xl font-bold text-foreground" data-testid="article-title">{article.title}</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{article.intro}</p>

        {article.body.map((sec) => (
          <section className="mt-8" key={sec.h2}>
            <h2 className="font-display text-xl font-bold text-foreground">{sec.h2}</h2>
            <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-muted-foreground">
              {sec.paragraphs.map((p) => (
                <p key={p.slice(0, 40)}>{p}</p>
              ))}
            </div>
          </section>
        ))}

        <div className="mt-10 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-bold text-foreground">Planifiez votre trajet en VSP</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Ouvrez vspo pour un itinéraire adapté à votre voiture sans permis, sans autoroute ni voie rapide.
          </p>
          <Link to="/" className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground">
            <Navigation2 className="h-4 w-4" /> Ouvrir le GPS
          </Link>
        </div>

        {others.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-base font-bold text-foreground">À lire aussi</h2>
            <div className="mt-3 space-y-2">
              {others.map((a) => (
                <Link key={a.slug} to={`${BLOG_PATH}/${a.slug}`} className="block text-sm text-primary hover:underline" data-testid={`related-${a.slug}`}>
                  {a.title}
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-8">
          <h2 className="font-display text-base font-bold text-foreground">Guides pratiques</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {THEMATIC_PAGES.map((t) => (
              <Link
                key={t.slug}
                to={t.path}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-primary transition-colors hover:bg-muted"
                data-testid={`article-thematic-${t.slug}`}
              >
                {t.kicker}
              </Link>
            ))}
          </div>
        </section>

        <p className="mt-8 text-xs text-muted-foreground">
          Informations fournies à titre indicatif. Vérifiez toujours les conditions de votre contrat et
          les préconisations du constructeur.
        </p>
      </main>
      <SeoFooter />
    </div>
  );
}
